import React, { useEffect, useRef, useState } from "react";
import { FaPills } from "react-icons/fa6";
import { FiBell, FiClock, FiDroplet, FiMessageCircle, FiSun } from "react-icons/fi";
import SteffiDevice from "../components/SteffiDevice";
import PhoneMock, { useMockScreens } from "../components/PhoneMock";
import DeviceScreenFrame from "../components/DeviceScreenFrame";
import Reveal, { useInView, useScrollProgress } from "../components/Reveal";
import { APP_SHOTS, SCREENS, useLocalizedAsset } from "../renders";
import { useSteffiCopy } from "../content";

/* The chapter order is the same in both languages, so the scroll maths can
   stay language-independent. */
const CHAPTER_ORDER = ["feed", "calendar", "reminders", "routines"];
const N = CHAPTER_ORDER.length;

function Phone({ shot, alt, className = "", style }) {
  const localize = useLocalizedAsset();
  const s = localize(APP_SHOTS[shot]);
  const mock = useMockScreens();
  if (mock) {
    return (
      <PhoneMock screen={shot} alt={alt} className={`phone-mock--tall ${className}`} style={style} />
    );
  }
  return (
    <img
      src={s.src}
      width={s.w}
      height={s.h}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={`h-full w-auto max-w-full object-contain ${className}`}
      style={{ filter: "drop-shadow(0 40px 70px rgba(36,33,30,.22))", ...style }}
    />
  );
}

function Points({ items, className = "" }) {
  return (
    <ul className={`mt-8 flex flex-wrap gap-2 ${className}`}>
      {items.map((point) => (
        <li
          key={point}
          className="rounded-full border border-[color:var(--border)] bg-white/70 px-4 py-2 text-[0.9375rem] text-[color:var(--ink-2)]"
        >
          {point}
        </li>
      ))}
    </ul>
  );
}

const REMINDERS_CHAPTER = CHAPTER_ORDER.indexOf("reminders");

/* Chapters where Steffi stands in the room next to the phone. Only the
   reminders chapter drives her display; in the others she just looks on. */
const CHAPTERS_WITH_STEFFI = new Set(["feed", "calendar", "reminders", "routines"]);
const CHAPTER_HANDOFFS = {
  feed: {
    key: "family-feed",
    icon: "message",
    screens: ["familyFeed"],
  },
  routines: {
    key: "activity",
    icon: "activity",
    screens: ["activity", "exercise"],
  },
};

const REMINDER_ICONS = {
  pills: FaPills,
  bell: FiBell,
  timer: FiClock,
  drink: FiDroplet,
  activity: FiSun,
  message: FiMessageCircle,
};

/* Not every reminder has a screen shot yet. Probe them once, so a missing file
   simply leaves Steffi's face on rather than showing a broken image. */
function useScreenAvailability(reminders) {
  const localize = useLocalizedAsset();
  const [ready, setReady] = useState({});

  useEffect(() => {
    let cancelled = false;
    const screenKeys = [
      ...reminders.map(({ screen }) => screen),
      ...Object.values(CHAPTER_HANDOFFS).flatMap(({ screens }) => screens),
    ];
    [...new Set(screenKeys)].forEach((screen) => {
      const src = localize(SCREENS[screen]);
      if (!src) return;
      const image = new Image();
      image.onload = () => !cancelled && setReady((r) => ({ ...r, [screen]: true }));
      image.onerror = () => !cancelled && setReady((r) => ({ ...r, [screen]: false }));
      image.src = src;
    });
    return () => {
      cancelled = true;
    };
  }, [reminders, localize]);

  return ready;
}

const TRAVEL_MS = 1900;
const ON_SCREEN_MS = 2600;
const FACE_HOLD_MS = 1200;

/* One reminder at a time: it leaves the phone, lands on Steffi, and her display
   takes it over. Only runs while the chapter is actually on screen. */
function useReminderCycle(active, REMINDERS) {
  const [step, setStep] = useState(0);
  const [landed, setLanded] = useState(false);

  useEffect(() => {
    if (!active) {
      setStep(0);
      setLanded(false);
      return undefined;
    }
    if (!landed) {
      const t = setTimeout(() => setLanded(true), TRAVEL_MS);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setLanded(false);
      setStep((s) => (s + 1) % REMINDERS.length);
    }, ON_SCREEN_MS);
    return () => clearTimeout(t);
  }, [active, landed, step, REMINDERS.length]);

  return { item: REMINDERS[step], step, landed };
}

/* Feed and routine chapters each tell a small, repeatable story: Steffi looks
   on, an item travels over from the app, and the matching screen takes over. */
function useChapterHandoff(chapterShot) {
  const active = Boolean(CHAPTER_HANDOFFS[chapterShot]);
  const [phase, setPhase] = useState("face");
  const [cycle, setCycle] = useState(0);
  const [screenIndex, setScreenIndex] = useState(0);

  useEffect(() => {
    setPhase("face");
    setCycle(0);
    setScreenIndex(0);
  }, [chapterShot]);

  useEffect(() => {
    if (!active) return undefined;

    const duration =
      phase === "face" ? FACE_HOLD_MS : phase === "travel" ? TRAVEL_MS : ON_SCREEN_MS;
    const timer = setTimeout(() => {
      if (phase === "face") setPhase("travel");
      else if (phase === "travel") setPhase("screen");
      else {
        const screens = CHAPTER_HANDOFFS[chapterShot]?.screens || [];
        if (screenIndex + 1 < screens.length) {
          setScreenIndex((value) => value + 1);
        } else {
          setPhase("face");
          setScreenIndex(0);
          setCycle((value) => value + 1);
        }
      }
    }, duration);

    return () => clearTimeout(timer);
  }, [active, chapterShot, phase, screenIndex]);

  return {
    item: CHAPTER_HANDOFFS[chapterShot],
    phase: active ? phase : "face",
    cycle,
    screen: CHAPTER_HANDOFFS[chapterShot]?.screens[screenIndex],
  };
}

/* The reminder leaving the phone and arriving in the room. */
function DeliveryBubble({ item, label, step, visible }) {
  if (!item) return null;
  const Icon = REMINDER_ICONS[item.icon] || FiBell;
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-visible">
      <span
        // keyed by step so the flight restarts cleanly for every reminder
        key={step}
        // starts on the phone, lands on Steffi's glass
        className="absolute left-[26%] top-[22%] inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[color:var(--border)] bg-white px-5 py-3 text-[1rem] font-medium text-[color:var(--ink)] shadow-[0_20px_44px_-22px_rgba(36,33,30,.6)]"
        style={{
          "--tx": "-12.8rem",
          "--ty": "11.68rem",
          animation: visible
            ? `steffi-deliver ${TRAVEL_MS}ms cubic-bezier(.2,.8,.2,1) both`
            : "none",
          opacity: visible ? undefined : 0,
        }}
      >
        <Icon className="h-[1.15rem] w-[1.15rem] text-[color:var(--color-highlight)]" aria-hidden />
        {label ?? item.label}
      </span>
    </div>
  );
}

/* Use the same recessed bezel and calibrated inner viewport as the large
   activity device. The supplied asset already has transparent corners, so it
   only needs to fill that shared inner circle. */
function DeviceScreenImage({ src, shown = true }) {
  if (!src) return null;
  return (
    <DeviceScreenFrame>
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          opacity: shown ? 1 : 0,
          transform: `scale(${shown ? 1 : 1.06})`,
          transition:
            "opacity 600ms cubic-bezier(.2,.8,.2,1), transform 900ms cubic-bezier(.2,.8,.2,1)",
        }}
      >
        <img src={src} alt="" className="h-full w-full object-cover" />
      </div>
    </DeviceScreenFrame>
  );
}

/* What Steffi puts on her glass once the reminder lands. */
function ReminderScreen({ item, shown, available }) {
  const localize = useLocalizedAsset();
  const src = localize(SCREENS[item.screen]);
  if (!src || !available) return null;
  return <DeviceScreenImage src={src} shown={shown} />;
}

export default function AppSection() {
  const mock = useMockScreens();
  const { APP, REMINDERS } = useSteffiCopy();
  const localize = useLocalizedAsset();
  const railRef = useRef(null);
  const [stickyRef, railInView] = useInView({ threshold: 0.2, rootMargin: "0px", once: false });
  const progress = useScrollProgress(railRef);
  const pos = progress * N;
  const activeIndex = Math.min(N - 1, Math.max(0, Math.floor(pos)));

  /* the reminders chapter drives Steffi's display; the others just show her */
  const activeChapterShot = CHAPTER_ORDER[activeIndex];
  const onReminders = railInView && activeIndex === REMINDERS_CHAPTER;
  const showSteffi = CHAPTERS_WITH_STEFFI.has(activeChapterShot);
  const chapterHandoff = useChapterHandoff(railInView ? activeChapterShot : null);
  const reminder = useReminderCycle(onReminders, REMINDERS);
  const screenReady = useScreenAvailability(REMINDERS);
  const reminderScreenIsUp = reminder.landed && screenReady[reminder.item.screen] === true;
  const chapterScreenSrc = chapterHandoff.screen && localize(SCREENS[chapterHandoff.screen]);
  const chapterScreenIsUp =
    chapterHandoff.phase === "screen" &&
    Boolean(chapterScreenSrc) &&
    screenReady[chapterHandoff.screen] === true;
  const deviceScreenIsUp = reminderScreenIsUp || chapterScreenIsUp;

  /* A chapter holds at full strength, then hands over to its neighbour.
     The ramp is shaped so the two overlapping opacities always sum to 1 —
     without it the phone visibly dims at every chapter boundary. */
  const FADE = 0.45;
  const weight = (i) => {
    const d = progress * N - (i + 0.5);
    const o = (0.5 + FADE / 2 - Math.abs(d)) / FADE;
    return { d, o: Math.max(0, Math.min(1, o)) };
  };

  return (
    <>
      {/* ---------------------------------------- the perspective changes here */}
      <section
        id="app"
        className="relative overflow-hidden pb-10 pt-28 lg:pb-12 lg:pt-40"
        style={{ background: "var(--bg)" }}
      >
        <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-10">
          <div className="mx-auto max-w-[82rem] text-center">
            <Reveal as="p" className="t-eyebrow">
              {APP.eyebrow}
            </Reveal>
            <Reveal
              as="h2"
              delay={80}
              className="t-section mt-7 !text-[clamp(3.5rem,7vw,7rem)] !leading-[0.94]"
            >
              {APP.title.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </Reveal>
            <Reveal
              as="p"
              delay={160}
              className="t-lead mx-auto mt-10 max-w-[62rem] text-[color:var(--ink-2)]"
            >
              {APP.intro}
            </Reveal>
            <Reveal delay={220} className="mt-14 lg:mt-20">
              {mock ? (
                <div className="phone-trio" role="img" aria-label={APP.alts.trio}>
                  <PhoneMock screen="reminders" />
                  <PhoneMock screen="feed" />
                  <PhoneMock screen="pick" />
                </div>
              ) : (
              <img
                src={APP.trioImage}
                width="2046"
                height="1844"
                alt={APP.alts.trio}
                loading="lazy"
                decoding="async"
                className="mx-auto block w-[84%] max-w-[68rem]"
                style={{ filter: "drop-shadow(0 42px 72px rgba(36,33,30,.16))" }}
              />
              )}
            </Reveal>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- sticky phone chapters */}
      <section
        ref={railRef}
        id="app-rail"
        className="relative hidden lg:block"
        style={{ height: `${N * 105}vh`, background: "var(--bg)" }}
      >
        {/* the sticky container opens a stacking context, so it carries the
            page colour itself */}
        <div
          ref={stickyRef}
          className="sticky top-0 flex h-screen items-center overflow-hidden"
          style={{ background: "var(--bg)" }}
        >
          <div className="mx-auto grid w-full max-w-[1440px] grid-cols-[1fr_0.85fr] items-center gap-20 px-10">
            <div className="relative h-[26rem]">
              {APP.chapters.map((chapter, i) => {
                const { d, o } = weight(i);
                return (
                  <div
                    key={chapter.title}
                    className="absolute inset-x-0 top-1/2 max-w-[34rem] -translate-y-1/2"
                    style={{
                      opacity: o,
                      transform: `translateY(calc(-50% + ${(d * -46).toFixed(1)}px))`,
                      filter: o > 0.98 ? "none" : `blur(${((1 - o) * 7).toFixed(2)}px)`,
                      pointerEvents: o > 0.5 ? "auto" : "none",
                      transition: "filter 300ms linear",
                    }}
                  >
                    <p className="t-eyebrow">{chapter.eyebrow}</p>
                    <h3 className="t-section mt-5 !text-[clamp(2.75rem,5.2vw,5rem)] !leading-[1.02]">
                      {chapter.title}
                    </h3>
                    <p className="mt-7 text-[clamp(1.125rem,1.55vw,1.5rem)] leading-[1.5] text-[color:var(--ink-2)]">
                      {chapter.body}
                    </p>
                    <Points items={chapter.points} />
                  </div>
                );
              })}
            </div>

            {/* `mx-auto` here collapsed the column to 0 px — every child is
                absolutely positioned, so it shrank to fit-content and the
                phone and bubble laid out against nothing. */}
            <div className="relative h-[80vh] max-h-[46rem] w-full">
              <div
                aria-hidden
                className="glow-blob absolute left-1/2 top-1/2 h-[26rem] w-[26rem] -translate-x-1/2 -translate-y-1/2"
                style={{
                  background:
                    "radial-gradient(circle, rgba(255,255,255,.85) 0%, rgba(236,224,211,0) 68%)",
                }}
              />
              <div className="relative h-full">
                {APP.chapters.map((chapter, i) => {
                  const { o } = weight(i);
                  return (
                    <div
                      key={chapter.shot}
                      className="absolute inset-0 flex items-center justify-center"
                      style={{
                        opacity: o,
                        transform: `scale(${(0.965 + o * 0.035).toFixed(4)})`,
                        transition: "opacity 260ms linear, transform 600ms cubic-bezier(.2,.8,.2,1)",
                      }}
                    >
                      <Phone shot={chapter.shot} alt={APP.alts.phone(chapter.title)} />
                    </div>
                  );
                })}
              </div>

              <DeliveryBubble item={reminder.item} step={reminder.step} visible={onReminders} />
              <DeliveryBubble
                item={chapterHandoff.item}
                label={APP.handoffs[activeChapterShot]}
                step={chapterHandoff.cycle}
                visible={chapterHandoff.phase === "travel"}
              />

              {/* Steffi in the room, taking the reminder over onto her display */}
              {/* She stands just in front of the phone, sized against the
                  viewport so she never reaches across into the heading. */}
              {/* She stands just in front of the phone. Below 1280 the column
                  is too narrow to hold her at full size without running into
                  the heading, so she steps down a size there. */}
              <div className="absolute bottom-2 left-0 -ml-[6vw] w-[clamp(11rem,16vw,14rem)] xl:-ml-[11vw] xl:w-[clamp(14rem,23vw,22rem)]">
                <SteffiDevice
                  variant="green"
                  float={false}
                  awake={!deviceScreenIsUp}
                  speaking={reminderScreenIsUp}
                  followPointer={false}
                  screen={
                    onReminders && reminderScreenIsUp ? (
                      <ReminderScreen
                        item={reminder.item}
                        shown
                        available={screenReady[reminder.item.screen] === true}
                      />
                    ) : chapterScreenIsUp ? (
                      <DeviceScreenImage src={chapterScreenSrc} />
                    ) : null
                  }
                  alt=""
                  unitStyle={{
                    opacity: showSteffi ? 1 : 0,
                    transition: "opacity 800ms cubic-bezier(.2,.8,.2,1)",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- mobile */}
      <section className="lg:hidden" style={{ background: "var(--bg)" }}>
        <div className="mx-auto w-full max-w-[42rem] px-6 pb-24">
          {APP.chapters.map((chapter) => (
            <Reveal key={chapter.shot} className="border-t border-[color:var(--border)] py-16 first:border-t-0">
              <p className="t-eyebrow">{chapter.eyebrow}</p>
              <h3 className="t-section mt-4 !text-[2rem]">{chapter.title}</h3>
              <p className="t-body mt-5 text-[color:var(--ink-2)]">{chapter.body}</p>
              <Points items={chapter.points} />
              <div className="mt-10 flex h-[30rem] justify-center">
                <Phone shot={chapter.shot} alt={APP.alts.phone(chapter.title)} />
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
